import { tuning } from '@/config/tuning';
import { shapeAxis } from '@/input/touchTuning';
import { clamp } from '@/math/angles';

/**
 * Tilt.ts — optional gyro steering (DeviceOrientation). The player tilts the phone
 * left/right to steer while the joystick still handles the gas. Output is RAW
 * screen-space steer (+ = right), like every other input source, so the §6.2 flip
 * applies downstream unchanged.
 *
 * It only reports `active` while the scheme is enabled AND real orientation events
 * are arriving — so a desktop browser, a gyro-less device, or a denied iOS
 * permission all leave it inactive, and the InputManager's `strongest()` merge
 * falls back to the joystick automatically. The player is never left unable to steer.
 *
 * CAVEAT (flagged): uses `gamma` (portrait left-right roll). Landscape play would
 * need beta/gamma swapped per screen orientation — a later refinement.
 */
export interface TiltReading {
  steer: number; // -1..1, + = right (raw screen-space)
  active: boolean;
}

export class Tilt {
  private enabled = false;
  /** Calibrated centre roll (radians) — "the phone, held however I hold it". */
  private center = 0;
  /** Latest roll from the sensor (radians). */
  private roll = 0;
  /** True once at least one real event has arrived (gyro exists + permitted). */
  private received = false;

  constructor() {
    window.addEventListener('deviceorientation', this.onOrient);
  }

  private onOrient = (e: DeviceOrientationEvent): void => {
    if (e.gamma === null || e.gamma === undefined) return;
    this.roll = (e.gamma * Math.PI) / 180; // left-right tilt (deg) → radians
    this.received = true;
  };

  /** Capture the current roll as the steering centre. Called when a race's controls
   *  come on and from the Settings tap, so "level" = however the phone is held. */
  calibrate(): void {
    this.center = this.roll;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (enabled) this.calibrate();
  }

  /**
   * iOS 13+ hides motion behind a permission prompt that MUST be triggered from a
   * user gesture (the Settings tap). Elsewhere (Android / older iOS) there's no
   * prompt. Resolves to whether motion is usable.
   */
  async requestPermission(): Promise<boolean> {
    const DOE = DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };
    if (typeof DOE.requestPermission === 'function') {
      try {
        return (await DOE.requestPermission()) === 'granted';
      } catch {
        return false;
      }
    }
    return true;
  }

  read(): TiltReading {
    if (!this.enabled || !this.received) return { steer: 0, active: false };
    const t = tuning.touch;
    // Roll relative to centre, normalized so ±tiltMaxTilt = full throw, then shaped
    // (deadzone + curve) exactly like the joystick for a consistent feel.
    const norm = clamp((this.roll - this.center) / t.tiltMaxTilt, -1, 1);
    return { steer: shapeAxis(norm, t.tiltDeadzone, t.steerExpo, t.tiltSensitivity), active: true };
  }

  dispose(): void {
    window.removeEventListener('deviceorientation', this.onOrient);
  }
}
